import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';
import relativeTime from 'dayjs/plugin/relativeTime.js';

// src/result.ts
var ok = (value) => ({ ok: true, value });
var err = (error) => ({ ok: false, error });
var isOk = (r) => r.ok;
var isErr = (r) => !r.ok;

// src/errors/app-error.ts
var AppError = class extends Error {
  statusCode;
  errorCode;
  fieldErrors;
  constructor(message, statusCode = 500, errorCode = "INTERNAL_ERROR", fieldErrors) {
    super(message);
    this.name = this.constructor.name;
    this.statusCode = statusCode;
    this.errorCode = errorCode;
    this.fieldErrors = fieldErrors;
    Error.captureStackTrace?.(this, this.constructor);
  }
};
var NotFoundError = class extends AppError {
  constructor(message = "Resource not found") {
    super(message, 404, "NOT_FOUND");
  }
};
var ValidationError = class extends AppError {
  constructor(message = "Validation failed", fieldErrors) {
    super(message, 422, "VALIDATION_ERROR", fieldErrors);
  }
};
var UnauthorizedError = class extends AppError {
  constructor(message = "Unauthorized") {
    super(message, 401, "UNAUTHORIZED");
  }
};
var ForbiddenError = class extends AppError {
  constructor(message = "Forbidden") {
    super(message, 403, "FORBIDDEN");
  }
};
var ConflictError = class extends AppError {
  constructor(message = "Conflict") {
    super(message, 409, "CONFLICT");
  }
};

// src/http/response.ts
var buildSuccessResponse = (data, ctx) => ({
  success: true,
  statusCode: ctx.statusCode,
  message: ctx.message,
  data,
  timestamp: (/* @__PURE__ */ new Date()).toISOString(),
  path: ctx.path,
  traceId: ctx.traceId
});
var buildErrorResponse = (ctx) => ({
  success: false,
  statusCode: ctx.statusCode,
  message: ctx.message,
  errorCode: ctx.errorCode,
  errors: ctx.errors,
  timestamp: (/* @__PURE__ */ new Date()).toISOString(),
  path: ctx.path,
  traceId: ctx.traceId
});
var buildPaginationMeta = (page, pageSize, totalItems) => {
  const totalPages = pageSize > 0 ? Math.ceil(totalItems / pageSize) : 0;
  return {
    page,
    pageSize,
    totalItems,
    totalPages,
    hasPreviousPage: page > 1,
    hasNextPage: page < totalPages
  };
};
var buildPaginated = (items, page, pageSize, totalItems) => ({
  items,
  meta: buildPaginationMeta(page, pageSize, totalItems)
});
dayjs.extend(utc);
dayjs.extend(relativeTime);
var nowIso = () => dayjs().toISOString();
var toIso = (value) => dayjs(value).toISOString();
var formatDate = (value, format = "YYYY-MM-DD") => dayjs(value).format(format);
var addDays = (value, days) => dayjs(value).add(days, "day").toDate();
var diffInDays = (a, b) => dayjs(a).diff(dayjs(b), "day");
var isPast = (value) => dayjs(value).isBefore(dayjs());
var fromNow = (value) => dayjs(value).fromNow();

// src/utils/string.ts
var slugify = (input) => input.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-");
var capitalize = (input) => input.length === 0 ? input : input.charAt(0).toUpperCase() + input.slice(1);
var truncate = (input, max) => input.length <= max ? input : `${input.slice(0, Math.max(0, max - 1))}\u2026`;
var buildDocumentNumber = (prefix, year, sequence) => `${prefix}-${year}-${String(sequence).padStart(6, "0")}`;

// src/utils/guards.ts
var isDefined = (value) => value !== null && value !== void 0;
var assertNever = (value, message = "Unexpected value") => {
  throw new Error(`${message}: ${String(value)}`);
};
var ensureArray = (value) => Array.isArray(value) ? value : [value];

// src/utils/pricing.ts
var round2 = (value) => Math.round(value * 100) / 100;
var excludeGst = (inclusivePrice, gstRate) => round2(inclusivePrice / (1 + gstRate / 100));
var calculateTaxableBase = (purchaseRate, transportRate, additionalRate) => round2(purchaseRate + transportRate + additionalRate);
var calculateGstAmount = (purchaseRate, transportRate, additionalRate, gstRate) => round2(calculateTaxableBase(purchaseRate, transportRate, additionalRate) * (gstRate / 100));
var calculateLandingCost = (purchaseRate, transportRate, additionalRate, gstRate) => {
  const base = purchaseRate + transportRate + additionalRate;
  return round2(base * (1 + gstRate / 100));
};

// src/utils/quantity.ts
var round22 = (value) => Math.round(value * 100) / 100;
var splitBoxesPieces = (qtyBoxes, piecesPerBox) => {
  if (piecesPerBox <= 0) return { boxes: Math.trunc(qtyBoxes), pieces: 0 };
  const totalPieces = Math.round(qtyBoxes * piecesPerBox);
  return {
    boxes: Math.trunc(totalPieces / piecesPerBox),
    pieces: totalPieces % piecesPerBox
  };
};
var formatStockQuantity = (qtyBoxes, piecesPerBox, sqftPerBox, baseUom) => {
  if (baseUom === "PIECE" || piecesPerBox === 1) {
    return `${Math.round(qtyBoxes * piecesPerBox)} pcs`;
  }
  if (baseUom === "SQFT") {
    return `${round22(qtyBoxes * sqftPerBox)} sq.ft`;
  }
  const { boxes, pieces } = splitBoxesPieces(qtyBoxes, piecesPerBox);
  return pieces > 0 ? `${boxes} box ${pieces} pcs` : `${boxes} box`;
};
var formatBoxPieces = (qtyBoxes, piecesPerBox, pieceOnly = false) => {
  const perBox = piecesPerBox > 0 ? piecesPerBox : 1;
  if (pieceOnly || perBox === 1) return `${Math.round(qtyBoxes * perBox)} pcs`;
  const { boxes, pieces } = splitBoxesPieces(qtyBoxes, perBox);
  if (boxes === 0 && pieces === 0) return "0";
  if (boxes === 0) return `${pieces} pcs`;
  return pieces > 0 ? `${boxes} box ${pieces} pcs` : `${boxes} box`;
};

// src/utils/purchase.ts
var round23 = (value) => Math.round(value * 100) / 100;
var calculatePurchaseLine = (qtyBoxes, rate, discountPct, gstRate) => {
  const gross = qtyBoxes * rate;
  const lineSubTotal = round23(gross * (1 - discountPct / 100));
  const lineGst = round23(lineSubTotal * (gstRate / 100));
  return { lineSubTotal, lineGst, lineTotal: round23(lineSubTotal + lineGst) };
};
var sumPurchaseTotals = (lines) => {
  const subTotal = round23(lines.reduce((sum, l) => sum + l.lineSubTotal, 0));
  const gstAmount = round23(lines.reduce((sum, l) => sum + l.lineGst, 0));
  return { subTotal, gstAmount, grandTotal: round23(subTotal + gstAmount) };
};
var apportionCharge = (lineValues, totalCharge) => {
  const total2 = lineValues.reduce((sum, value) => sum + value, 0);
  if (total2 <= 0 || totalCharge <= 0) return lineValues.map(() => 0);
  return lineValues.map((value) => round23(value / total2 * totalCharge));
};

// src/utils/money-words.ts
var ONES = [
  "",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
  "Eleven",
  "Twelve",
  "Thirteen",
  "Fourteen",
  "Fifteen",
  "Sixteen",
  "Seventeen",
  "Eighteen",
  "Nineteen"
];
var TENS = [
  "",
  "",
  "Twenty",
  "Thirty",
  "Forty",
  "Fifty",
  "Sixty",
  "Seventy",
  "Eighty",
  "Ninety"
];
function underThousand(value) {
  if (value < 20) return ONES[value] ?? "";
  if (value < 100) {
    const tens = TENS[Math.floor(value / 10)] ?? "";
    const ones = ONES[value % 10] ?? "";
    return ones ? `${tens} ${ones}` : tens;
  }
  const hundreds = `${ONES[Math.floor(value / 100)]} Hundred`;
  const rest = underThousand(value % 100);
  return rest ? `${hundreds} ${rest}` : hundreds;
}
function numberToIndianWords(value) {
  const whole = Math.floor(Math.abs(value));
  if (whole === 0) return "Zero";
  const crore = Math.floor(whole / 1e7);
  const lakh = Math.floor(whole % 1e7 / 1e5);
  const thousand = Math.floor(whole % 1e5 / 1e3);
  const rest = whole % 1e3;
  const parts = [];
  if (crore > 0) parts.push(`${numberToIndianWords(crore)} Crore`);
  if (lakh > 0) parts.push(`${underThousand(lakh)} Lakh`);
  if (thousand > 0) parts.push(`${underThousand(thousand)} Thousand`);
  if (rest > 0) parts.push(underThousand(rest));
  return parts.join(" ");
}
function amountInWords(amount) {
  const negative = amount < 0;
  const absolute = Math.round((Math.abs(amount) + Number.EPSILON) * 100) / 100;
  const rupees = Math.floor(absolute);
  const paise = Math.round((absolute - rupees) * 100);
  const words = [`Rupees ${numberToIndianWords(rupees)}`];
  if (paise > 0) words.push(`and ${numberToIndianWords(paise)} Paise`);
  words.push("Only");
  return `${negative ? "Minus " : ""}${words.join(" ")}`;
}

// src/utils/gst.ts
var round24 = (value) => Math.round(value * 100) / 100;
function splitGst(gstAmount, interState) {
  const total2 = round24(gstAmount);
  if (interState) {
    return { cgst: 0, sgst: 0, igst: total2, total: total2 };
  }
  const cgst = round24(total2 / 2);
  return { cgst, sgst: round24(total2 - cgst), igst: 0, total: total2 };
}
function isInterStateSupply(branchStateCode, customerStateCode) {
  if (!branchStateCode || !customerStateCode) return false;
  return branchStateCode.trim() !== customerStateCode.trim();
}

// src/utils/csv.ts
var cell = (value) => {
  if (value === null || value === void 0) return "";
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : "";
  if (typeof value === "boolean") return value ? "TRUE" : "FALSE";
  const text = value;
  const risky = /^[=+\-@\t\r]/.test(text);
  const body = risky ? `'${text}` : text;
  return /[",\r\n]/.test(body) ? `"${body.replace(/"/g, '""')}"` : body;
};
var toCsv = (headers, rows) => [headers, ...rows].map((row) => row.map(cell).join(",")).join("\r\n");

// src/utils/gstr1.ts
function columnLetter(index) {
  let letters = "";
  for (let n = index; n >= 0; n = Math.floor(n / 26) - 1) {
    letters = String.fromCharCode(65 + n % 26) + letters;
  }
  return letters;
}
function summarise(summary, rows) {
  const values = rows.map((row) => row[summary.column]);
  if (summary.kind === "count") {
    return new Set(
      values.filter((value) => value !== null && value !== void 0 && value !== "")
    ).size;
  }
  return values.reduce(
    (total2, value) => total2 + (typeof value === "number" && Number.isFinite(value) ? value : 0),
    0
  );
}
var total = (label, column, lastRow = 1048576) => ({
  label,
  column,
  kind: "total",
  lastRow
});
var count = (label, column, lastRow = 20004) => ({
  label,
  column,
  kind: "count",
  lastRow
});
var HSN_HEADERS = [
  "HSN",
  "Description",
  "UQC",
  "Total Quantity",
  "Total Value",
  "Rate",
  "Taxable Value",
  "Integrated Tax Amount",
  "Central Tax Amount",
  "State/UT Tax Amount",
  "Cess Amount"
];
var HSN_SUMMARY = [
  count("No. of HSN", 0, 2e3),
  total("Total Value", 4, 2e3),
  total("Total Taxable Value", 6, 2e3),
  total("Total Integrated Tax", 7, 2e3),
  total("Total Central Tax", 8, 2e3),
  total("Total State/UT Tax", 9, 2e3),
  total("Total Cess", 10, 2e3)
];
var hsnRows = (key) => (data) => data[key].map((row) => [
  row.hsn,
  row.description,
  row.uqc,
  row.totalQuantity,
  row.totalValue,
  row.rate,
  row.taxableValue,
  row.integratedTax,
  row.centralTax,
  row.stateTax,
  row.cessAmount
]);
var GSTR1_SECTIONS = [
  {
    key: "b2b",
    sheet: "b2b,sez,de",
    label: "B2B \u2014 registered customers (4A, 4B, 6B, 6C)",
    title: "Summary For B2B, SEZ, DE (4A, 4B, 6B, 6C)",
    headers: [
      "GSTIN/UIN of Recipient",
      "Receiver Name",
      "Invoice Number",
      "Invoice date",
      "Invoice Value",
      "Place Of Supply",
      "Reverse Charge",
      "Applicable % of Tax Rate",
      "Invoice Type",
      "E-Commerce GSTIN",
      "Rate",
      "Taxable Value",
      "Cess Amount"
    ],
    summary: [
      count("No. of Recipients", 0),
      count("No. of Invoices", 2),
      total("Total Invoice Value", 4),
      total("Total Taxable Value", 11),
      total("Total Cess", 12)
    ],
    rows: (data) => data.b2b.map((row) => [
      row.gstin,
      row.receiverName,
      row.invoiceNumber,
      row.invoiceDate,
      row.invoiceValue,
      row.placeOfSupply,
      row.reverseCharge,
      "",
      row.invoiceType,
      "",
      row.rate,
      row.taxableValue,
      row.cessAmount
    ])
  },
  {
    key: "b2cl",
    sheet: "b2cl",
    label: "B2CL \u2014 large inter-state, unregistered (5)",
    title: "Summary For B2CL(5)",
    headers: [
      "Invoice Number",
      "Invoice date",
      "Invoice Value",
      "Place Of Supply",
      "Applicable % of Tax Rate",
      "Rate",
      "Taxable Value",
      "Cess Amount",
      "E-Commerce GSTIN"
    ],
    summary: [
      count("No. of Invoices", 0),
      total("Total Invoice Value", 2),
      total("Total Taxable Value", 6),
      total("Total Cess", 7)
    ],
    rows: (data) => data.b2cl.map((row) => [
      row.invoiceNumber,
      row.invoiceDate,
      row.invoiceValue,
      row.placeOfSupply,
      "",
      row.rate,
      row.taxableValue,
      row.cessAmount,
      ""
    ])
  },
  {
    key: "b2cs",
    sheet: "b2cs",
    label: "B2CS \u2014 small unregistered, totalled (7)",
    title: "Summary For B2CS(7)",
    headers: [
      "Type",
      "Place Of Supply",
      "Applicable % of Tax Rate",
      "Rate",
      "Taxable Value",
      "Cess Amount",
      "E-Commerce GSTIN"
    ],
    // The template spells this label with two spaces. Left as it is: the sheet is read
    // positionally, so a "corrected" label would only disagree with the tool's own.
    summary: [total("Total Taxable  Value", 4), total("Total Cess", 5)],
    rows: (data) => data.b2cs.map((row) => [
      row.type,
      row.placeOfSupply,
      "",
      row.rate,
      row.taxableValue,
      row.cessAmount,
      ""
    ])
  },
  {
    key: "hsnB2b",
    sheet: "hsn(b2b)",
    label: "HSN \u2014 B2B supplies (12)",
    title: "Summary For HSN(12)",
    headers: HSN_HEADERS,
    summary: HSN_SUMMARY,
    rows: hsnRows("hsnB2b")
  },
  {
    key: "hsnB2c",
    sheet: "hsn(b2c)",
    label: "HSN \u2014 B2C supplies (12)",
    title: "Summary For HSN(12)",
    headers: HSN_HEADERS,
    summary: HSN_SUMMARY,
    rows: hsnRows("hsnB2c")
  },
  {
    key: "docs",
    sheet: "docs",
    label: "Documents issued (13)",
    title: "Summary of documents issued during the tax period (13)",
    headers: ["Nature of Document", "Sr. No. From", "Sr. No. To", "Total Number", "Cancelled"],
    summary: [total("Total Number", 3), total("Total Cancelled", 4)],
    rows: (data) => data.docs.map((row) => [
      row.natureOfDocument,
      row.serialFrom,
      row.serialTo,
      row.totalNumber,
      row.cancelled
    ])
  }
];

// src/utils/ageing.ts
var AGEING_BUCKETS = [
  "current",
  "days30",
  "days60",
  "days90",
  "older"
];
function ageingBucketFor(days) {
  if (days <= 0) return "current";
  if (days <= 30) return "days30";
  if (days <= 60) return "days60";
  if (days <= 90) return "days90";
  return "older";
}
var DAY = 24 * 60 * 60 * 1e3;
function overdueDays(dueDate, documentDate, now = Date.now()) {
  const from = dueDate ?? documentDate;
  return Math.floor((now - from.getTime()) / DAY);
}
function isOverdue(dueDate, documentDate, now = Date.now()) {
  return overdueDays(dueDate, documentDate, now) > 0;
}
var AGEING_BUCKET_LABELS = {
  current: "Not due",
  days30: "1\u201330 d",
  days60: "31\u201360 d",
  days90: "61\u201390 d",
  older: "90+ d"
};

// src/utils/day-range.ts
function startOfDayIso(localDate) {
  return (/* @__PURE__ */ new Date(`${localDate}T00:00:00.000+05:30`)).toISOString();
}
function endOfDayIso(localDate) {
  return (/* @__PURE__ */ new Date(`${localDate}T23:59:59.999+05:30`)).toISOString();
}
function toDateInput(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// src/utils/transfer-doc.ts
var round25 = (value) => Math.round(value * 100) / 100;
var round3 = (value) => Math.round(value * 1e3) / 1e3;
var EWAY_BILL_THRESHOLD = 5e4;
function transferDocumentType(fromGstin, toGstin) {
  const from = fromGstin?.trim();
  const to = toGstin?.trim();
  if (!from || !to) return "DELIVERY_CHALLAN";
  return from.toUpperCase() === to.toUpperCase() ? "DELIVERY_CHALLAN" : "TAX_INVOICE";
}
function valueTransferLine(line, taxable) {
  const lineSubTotal = round25(round3(line.qtyBoxes) * line.rate);
  const lineGst = taxable ? round25(lineSubTotal * line.gstRate / 100) : 0;
  return { lineSubTotal, lineGst, lineTotal: round25(lineSubTotal + lineGst) };
}
function transferTotals(lines, interState) {
  const subTotal = round25(lines.reduce((sum, line) => sum + line.lineSubTotal, 0));
  const gstAmount = round25(lines.reduce((sum, line) => sum + line.lineGst, 0));
  const split = splitGst(gstAmount, interState);
  return {
    subTotal,
    cgstAmount: split.cgst,
    sgstAmount: split.sgst,
    igstAmount: split.igst,
    gstAmount: split.total,
    grandTotal: round25(subTotal + split.total)
  };
}
function needsEwayBill(consignmentValue) {
  return round25(consignmentValue) > EWAY_BILL_THRESHOLD;
}
function shortQty(sent, received) {
  return Math.max(0, round3(sent - received));
}
function isOpenTransfer(status) {
  return status === "IN_TRANSIT";
}

// src/utils/settlement.ts
var round26 = (value) => Math.round(value * 100) / 100;
var MONEY_EPSILON = 5e-3;
function settleOldestFirst(amount, open) {
  let remaining = round26(amount);
  const settlements = [];
  for (const target of open) {
    if (remaining <= MONEY_EPSILON) break;
    const settled = round26(Math.min(target.balanceAmount, remaining));
    if (settled <= 0) continue;
    settlements.push({ target, amount: settled });
    remaining = round26(remaining - settled);
  }
  return { settlements, remaining: round26(remaining) };
}
function exceeds(amount, limit) {
  return round26(amount) > round26(limit) + MONEY_EPSILON;
}
function isOutstanding(balance) {
  return round26(balance) > MONEY_EPSILON;
}

// src/utils/allocation.ts
var round32 = (value) => Math.round(value * 1e3) / 1e3;
function preferHomeBranch(sources, homeBranchId) {
  return [
    ...sources.filter((source) => source.branchId === homeBranchId),
    ...sources.filter((source) => source.branchId !== homeBranchId)
  ];
}
function drawFromSources(qtyBoxes, sources) {
  let outstanding = round32(qtyBoxes);
  const draws = [];
  for (const source of sources) {
    if (outstanding <= 0) break;
    if (source.availableQtyBoxes <= 0) continue;
    const take = round32(Math.min(source.availableQtyBoxes, outstanding));
    outstanding = round32(outstanding - take);
    draws.push({ source, qtyBoxes: take });
  }
  return { draws, shortfall: Math.max(outstanding, 0) };
}
function supplyingBranches(draws) {
  const seen = [];
  for (const draw of draws) {
    if (!seen.includes(draw.source.branchId)) seen.push(draw.source.branchId);
  }
  return seen;
}
function qtyFromBranch(draws, branchId) {
  return round32(
    draws.filter((draw) => draw.source.branchId === branchId).reduce((sum, draw) => sum + draw.qtyBoxes, 0)
  );
}

// src/utils/numbering.ts
function financialYearOf(date) {
  const year = date.getFullYear();
  const startYear = date.getMonth() >= 3 ? year : year - 1;
  const two = (value) => String(value % 100).padStart(2, "0");
  return `${two(startYear)}-${two(startYear + 1)}`;
}
function financialYearStart(date) {
  const year = date.getMonth() >= 3 ? date.getFullYear() : date.getFullYear() - 1;
  return new Date(year, 3, 1, 0, 0, 0, 0);
}
var DEFAULT_NUMBER_FORMAT = {
  separator: "/",
  padding: 4,
  resetAnnually: true
};
function formatDocumentNumber(format, sequence, financialYear, month) {
  const parts = [format.prefix.trim()];
  if ((format.resetAnnually || month) && financialYear) parts.push(financialYear);
  if (month) parts.push(month);
  parts.push(String(Math.max(sequence, 0)).padStart(Math.max(format.padding, 1), "0"));
  return parts.filter(Boolean).join(format.separator);
}
function previewDocumentNumber(format, at = /* @__PURE__ */ new Date()) {
  return formatDocumentNumber(format, 1, financialYearOf(at));
}
function isValidPrefix(prefix) {
  const trimmed = prefix.trim();
  if (trimmed.length === 0 || trimmed.length > 12) return false;
  if (trimmed !== prefix) return false;
  return /^[A-Za-z0-9][A-Za-z0-9 _-]*$/.test(trimmed);
}
function indiaNumberingPeriod(at) {
  const day = new Date(at.getTime() + 330 * 6e4).toISOString();
  const year = Number(day.slice(0, 4));
  const month = day.slice(5, 7);
  const start = Number(month) >= 4 ? year : year - 1;
  const two = (n) => String(n % 100).padStart(2, "0");
  return { period: day.slice(0, 7), month, financialYear: `${two(start)}-${two(start + 1)}` };
}

// src/utils/cash-book.ts
var round27 = (value) => Math.round(value * 100) / 100;
function signedAmount(movement) {
  return movement.direction === "IN" ? round27(movement.amount) : -round27(movement.amount);
}
function runningBalance(opening, entries) {
  let balance = round27(opening);
  const rows = entries.map((entry) => {
    balance = round27(balance + signedAmount(entry));
    return { entry, balance };
  });
  return { rows, closing: balance };
}
function cashTotals(entries) {
  const received = round27(
    entries.filter((e) => e.direction === "IN").reduce((sum, e) => sum + e.amount, 0)
  );
  const paid = round27(
    entries.filter((e) => e.direction === "OUT").reduce((sum, e) => sum + e.amount, 0)
  );
  return { received, paid, net: round27(received - paid) };
}
function wouldOverdraw(balance, amount) {
  return round27(balance) - round27(amount) < -5e-3;
}
function transferProblem(fromAccountId, toAccountId, amount) {
  if (!fromAccountId || !toAccountId) return "Choose both accounts";
  if (fromAccountId === toAccountId) {
    return "The two accounts must differ \u2014 money cannot move to where it already is";
  }
  if (round27(amount) <= 0) return "Enter an amount greater than zero";
  return null;
}
function cashVariance(counted, expected) {
  return round27(counted - expected);
}

// src/utils/day-close.ts
var round28 = (value) => Math.round(value * 100) / 100;
var DENOMINATIONS = [2e3, 500, 200, 100, 50, 20, 10, 5, 2, 1];
function denominationTotal(counts) {
  return round28(
    DENOMINATIONS.reduce(
      (total2, denomination) => total2 + denomination * (counts[denomination] ?? 0),
      0
    )
  );
}
function hasCounted(counts) {
  return DENOMINATIONS.some((denomination) => (counts[denomination] ?? 0) > 0);
}
function isDayLocked(entryDate, lastCloseDate) {
  if (!lastCloseDate) return false;
  return indiaBusinessDay(entryDate) <= indiaBusinessDay(lastCloseDate);
}
var indiaBusinessDay = (date) => Math.floor((date.getTime() + 330 * 60 * 1e3) / (24 * 60 * 60 * 1e3));
function closeProblem(input) {
  const today = input.today ?? /* @__PURE__ */ new Date();
  if (indiaBusinessDay(input.closeDate) > indiaBusinessDay(today)) {
    return "That day has not happened yet";
  }
  if (input.lastCloseDate && indiaBusinessDay(input.closeDate) <= indiaBusinessDay(input.lastCloseDate)) {
    return "That day is already closed \u2014 reopen it if the count was wrong";
  }
  if (input.countedAmount < 0) {
    return "A drawer cannot hold less than nothing";
  }
  return null;
}
function varianceVerdict(variance, tolerance = 1) {
  if (Math.abs(round28(variance)) <= tolerance) return "BALANCED";
  return variance < 0 ? "SHORT" : "OVER";
}
function handoverPlan(counted, float, roundTo = 100) {
  const available = round28(counted) - Math.max(0, round28(float));
  if (available <= 0) return { handover: 0, retained: round28(counted) };
  const step = roundTo > 0 ? roundTo : 1;
  const handover = Math.floor(available / step) * step;
  return { handover: round28(handover), retained: round28(round28(counted) - handover) };
}
function handoverProblem(handover, counted, ownerAccountId) {
  const amount = round28(handover);
  if (amount <= 0) return null;
  if (amount > round28(counted) + 5e-3) {
    return `Only ${round28(counted).toFixed(2)} was counted \u2014 that is all there is to hand over`;
  }
  if (!ownerAccountId) return "Choose who is taking the cash";
  return null;
}
function adjustmentFor(variance) {
  const amount = round28(Math.abs(variance));
  if (amount < 5e-3) return null;
  return { direction: variance > 0 ? "IN" : "OUT", amount };
}

// src/utils/tile-area.ts
var round4 = (value) => Math.round(value * 1e4) / 1e4;
var SQ_MM_PER_SQ_FT = 92903.04;
function parseSizeMm(sizeMm) {
  if (!sizeMm) return null;
  const match = /(\d+(?:\.\d+)?)\s*[x×*]\s*(\d+(?:\.\d+)?)/i.exec(sizeMm);
  if (!match) return null;
  const lengthMm = Number(match[1]);
  const widthMm = Number(match[2]);
  if (!Number.isFinite(lengthMm) || !Number.isFinite(widthMm)) return null;
  if (lengthMm <= 0 || widthMm <= 0) return null;
  return { lengthMm, widthMm };
}
function sqftPerPiece(size) {
  return round4(size.lengthMm * size.widthMm / SQ_MM_PER_SQ_FT);
}
function expectedSqftPerBox(sizeMm, piecesPerBox) {
  const size = parseSizeMm(sizeMm);
  if (!size || piecesPerBox <= 0) return null;
  return round4(sqftPerPiece(size) * piecesPerBox);
}
function checkSqftPerBox(sizeMm, piecesPerBox, storedSqftPerBox, tolerance = 0.1) {
  const expected = expectedSqftPerBox(sizeMm, piecesPerBox);
  if (expected === null || expected <= 0) {
    return { verdict: "UNKNOWN", expected: null, ratio: null, likelyCause: null };
  }
  if (storedSqftPerBox <= 0) {
    return {
      verdict: "SUSPECT",
      expected,
      ratio: 0,
      likelyCause: "No area recorded at all"
    };
  }
  const ratio = round4(storedSqftPerBox / expected);
  if (Math.abs(ratio - 1) <= tolerance) {
    return { verdict: "OK", expected, ratio, likelyCause: null };
  }
  return { verdict: "SUSPECT", expected, ratio, likelyCause: nameTheMistake(ratio, piecesPerBox) };
}
function nameTheMistake(ratio, piecesPerBox) {
  const near = (value, target, slack = 0.15) => Math.abs(value / target - 1) <= slack;
  if (near(ratio, 929.03 / piecesPerBox) || near(ratio, 929.03)) {
    return "Looks like the tile area in cm\xB2 rather than square feet";
  }
  if (near(ratio, 1 / 10.7639)) {
    return "Looks like square metres rather than square feet";
  }
  if (near(ratio, 10.7639)) {
    return "Looks like square metres converted the wrong way";
  }
  if (near(ratio, piecesPerBox) && piecesPerBox > 1) {
    return "Looks like the area was multiplied by the piece count twice";
  }
  if (near(ratio, 1 / piecesPerBox) && piecesPerBox > 1) {
    return "Looks like the area of one piece rather than the whole box";
  }
  if (ratio > 100) return "Far larger than the size allows \u2014 check the units";
  if (ratio < 0.01) return "Far smaller than the size allows \u2014 check the units";
  return ratio > 1 ? "Larger than the size allows" : "Smaller than the size allows";
}

// src/utils/margin.ts
var round29 = (value) => Math.round(value * 100) / 100;
function marginOn(netRate, landingCost) {
  if (landingCost === null || !Number.isFinite(landingCost)) {
    return { amount: null, pct: null };
  }
  const amount = round29(netRate - landingCost);
  return { amount, pct: netRate > 0 ? round29(amount / netRate * 100) : 0 };
}
var money = (value) => value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
function checkPrice(input) {
  const margin = marginOn(input.netRate, input.landingCost);
  if (input.landingCost !== null && input.netRate < input.landingCost - 5e-3) {
    return {
      verdict: "BELOW_COST",
      margin,
      blocking: true,
      message: `${money(input.netRate)} is below the ${money(input.landingCost)} these goods cost. This sale loses ${money(Math.abs(margin.amount ?? 0))} a box.`
    };
  }
  if (input.minSellingPrice !== null && input.netRate < input.minSellingPrice - 5e-3) {
    return {
      verdict: "BELOW_MIN",
      margin,
      blocking: true,
      message: `${money(input.netRate)} is below the branch minimum of ${money(input.minSellingPrice)}.`
    };
  }
  const floor = input.marginFloorPct ?? 0;
  if (floor > 0 && margin.pct !== null && margin.pct < floor) {
    return {
      verdict: "THIN",
      margin,
      // A warning, not a wall. Thin is sometimes the right call — clearing old shade lots,
      // a first order from a customer worth having — and a rule that cannot be judged
      // gets worked around rather than followed.
      blocking: false,
      message: `${margin.pct.toFixed(1)}% margin is under the ${floor}% floor.`
    };
  }
  return { verdict: "OK", margin, blocking: false, message: null };
}
function totalMargin(lines) {
  let revenue = 0;
  let cost = 0;
  let linesWithoutCost = 0;
  for (const line of lines) {
    revenue = round29(revenue + line.netRate * line.qtyBoxes);
    if (line.landingCost === null) {
      linesWithoutCost += 1;
      continue;
    }
    cost = round29(cost + line.landingCost * line.qtyBoxes);
  }
  const margin = round29(revenue - cost);
  return {
    revenue,
    cost,
    margin,
    marginPct: revenue > 0 ? round29(margin / revenue * 100) : 0,
    linesWithoutCost
  };
}

// src/utils/credit.ts
var round210 = (value) => Math.round(value * 100) / 100;
var money2 = (value) => value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
function checkCredit(position) {
  const exposure = round210(position.outstanding + position.documentValue);
  const overLimitBy = position.creditLimit > 0 ? round210(Math.max(0, exposure - position.creditLimit)) : 0;
  const overdueDays2 = position.creditDays > 0 ? Math.max(0, position.oldestOverdueDays) : 0;
  const over = overLimitBy > 0;
  const late = overdueDays2 > 0;
  if (!over && !late) {
    return { verdict: "OK", exposure, overLimitBy: 0, overdueDays: 0, message: null };
  }
  const parts = [];
  if (over) {
    parts.push(
      `${money2(exposure)} against a limit of ${money2(position.creditLimit)} \u2014 over by ${money2(overLimitBy)}`
    );
  }
  if (late) {
    parts.push(
      `${money2(position.overdueAmount)} is ${overdueDays2} day${overdueDays2 === 1 ? "" : "s"} past the ${position.creditDays}-day term`
    );
  }
  return {
    verdict: over && late ? "BOTH" : over ? "OVER_LIMIT" : "OVERDUE",
    exposure,
    overLimitBy,
    overdueDays: overdueDays2,
    message: parts.join("; ")
  };
}
var DEFAULT_APPROVAL_LEVELS = [
  { level: 1, maxExcess: 5e4 },
  { level: 2, maxExcess: 2e5 },
  { level: 3, maxExcess: 0 }
];
function requiredLevel(overLimitBy, levels = DEFAULT_APPROVAL_LEVELS) {
  const ladder = [...levels].sort((a, b) => a.level - b.level);
  for (const rung of ladder) {
    if (rung.maxExcess === 0 || overLimitBy <= rung.maxExcess) return rung.level;
  }
  return ladder[ladder.length - 1]?.level ?? 1;
}
function canDecide(heldLevels, required) {
  return heldLevels.some((level) => level >= required);
}
function parseApprovalLevels(raw) {
  if (!raw?.trim()) return DEFAULT_APPROVAL_LEVELS;
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return DEFAULT_APPROVAL_LEVELS;
    const levels = parsed.map((entry) => entry).filter(
      (entry) => typeof entry.level === "number" && typeof entry.maxExcess === "number"
    ).map((entry) => ({ level: entry.level, maxExcess: entry.maxExcess }));
    return levels.length > 0 ? levels : DEFAULT_APPROVAL_LEVELS;
  } catch {
    return DEFAULT_APPROVAL_LEVELS;
  }
}

// src/utils/ui-date.ts
var BUSINESS_ZONE = "Asia/Kolkata";
function isIsoCalendarDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const date = /* @__PURE__ */ new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}
function formatUIDate(value, options = {}) {
  if (value === null || value === void 0 || value === "") return "\u2014";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return isIsoCalendarDate(value) ? value.split("-").reverse().join("-") : "\u2014";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "\u2014";
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone: options.timeZone ?? BUSINESS_ZONE, day: "2-digit", month: "2-digit", year: "numeric" }).formatToParts(date);
  return ["day", "month", "year"].map((key) => parts.find((part) => part.type === key)?.value ?? "").join("-");
}
function formatUIDateTime(value, options = {}) {
  if (value === null || value === void 0 || value === "") return "\u2014";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "\u2014";
  const timeZone = options.timeZone ?? BUSINESS_ZONE;
  const time = new Intl.DateTimeFormat("en-IN", { timeZone, hour: "2-digit", minute: "2-digit", ...options.timeStyle === "short" ? {} : { second: "2-digit" }, hour12: options.hour12 ?? true }).format(date);
  return `${formatUIDate(date, { timeZone })}, ${time}`;
}
function isoToDateField(value, withTime = false) {
  const [date, time] = value.split("T");
  if (!date || !isIsoCalendarDate(date)) return "";
  return date.split("-").reverse().join("-") + (withTime && time ? ` ${time.slice(0, 5)}` : "");
}
function dateFieldToIso(value, withTime = false) {
  if (value === "") return "";
  const match = /^(\d{2})-(\d{2})-(\d{4})(?: (\d{2}):(\d{2}))?$/.exec(value);
  if (!match) return null;
  const date = `${match[3]}-${match[2]}-${match[1]}`;
  if (!isIsoCalendarDate(date)) return null;
  if (withTime) {
    if (match[4] === void 0 || match[5] === void 0 || Number(match[4]) > 23 || Number(match[5]) > 59) return null;
    return `${date}T${match[4]}:${match[5]}`;
  }
  return match[4] === void 0 ? date : null;
}
function maskDateField(value, withTime = false) {
  if (/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2})?$/.test(value)) return isoToDateField(value, withTime);
  const digits = value.replace(/\D/g, "").slice(0, withTime ? 12 : 8);
  return digits.slice(0, 2) + (digits.length > 2 ? `-${digits.slice(2, 4)}` : "") + (digits.length > 4 ? `-${digits.slice(4, 8)}` : "") + (withTime && digits.length > 8 ? ` ${digits.slice(8, 10)}` : "") + (withTime && digits.length > 10 ? `:${digits.slice(10, 12)}` : "");
}

export { AGEING_BUCKETS, AGEING_BUCKET_LABELS, AppError, ConflictError, DEFAULT_APPROVAL_LEVELS, DEFAULT_NUMBER_FORMAT, DENOMINATIONS, EWAY_BILL_THRESHOLD, ForbiddenError, GSTR1_SECTIONS, MONEY_EPSILON, NotFoundError, SQ_MM_PER_SQ_FT, UnauthorizedError, ValidationError, addDays, adjustmentFor, ageingBucketFor, amountInWords, apportionCharge, assertNever, buildDocumentNumber, buildErrorResponse, buildPaginated, buildPaginationMeta, buildSuccessResponse, calculateGstAmount, calculateLandingCost, calculatePurchaseLine, calculateTaxableBase, canDecide, capitalize, cashTotals, cashVariance, checkCredit, checkPrice, checkSqftPerBox, closeProblem, columnLetter, dateFieldToIso, denominationTotal, diffInDays, drawFromSources, endOfDayIso, ensureArray, err, exceeds, excludeGst, expectedSqftPerBox, financialYearOf, financialYearStart, formatBoxPieces, formatDate, formatDocumentNumber, formatStockQuantity, formatUIDate, formatUIDateTime, fromNow, handoverPlan, handoverProblem, hasCounted, indiaNumberingPeriod, isDayLocked, isDefined, isErr, isInterStateSupply, isIsoCalendarDate, isOk, isOpenTransfer, isOutstanding, isOverdue, isPast, isValidPrefix, isoToDateField, marginOn, maskDateField, needsEwayBill, nowIso, numberToIndianWords, ok, overdueDays, parseApprovalLevels, parseSizeMm, preferHomeBranch, previewDocumentNumber, qtyFromBranch, requiredLevel, runningBalance, settleOldestFirst, shortQty, signedAmount, slugify, splitBoxesPieces, splitGst, sqftPerPiece, startOfDayIso, sumPurchaseTotals, summarise, supplyingBranches, toCsv, toDateInput, toIso, totalMargin, transferDocumentType, transferProblem, transferTotals, truncate, valueTransferLine, varianceVerdict, wouldOverdraw };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map